import React, { lazy, Suspense } from 'react'
import { Navigate, Route, Routes, useLocation, useSearchParams } from 'react-router-dom'

import { Loading } from '@makyoapp/makyo-ui'

import {
  DUMMY_ROUTE,
  LOGIN_ROUTE,
  ORG_ID_PARAM,
  ORG_ROUTE,
  PROJECT_ID_PARAM,
  PROJECT_ROUTE,
  USER_ORG_ROUTE,
  USER_ROUTE,
} from '@/shared/constants'

import AppModals from './AppModals'
import { PageDashboardError } from './components/PageNotFound'
import { SpreadSheetAuth } from './components/SpreadSheetAuth'
import { ToolbarV2 } from './components/ToolbarV2'
import { NotificationsProvider } from './context/NotificationsContext'
import { useOrgsContext } from './context/OrgsContext'
import useAuth from './hooks/useAuth'
import { lazyload } from './lib/lazy-load'

const LoginPage = lazyload(() => import('./pages/login/Login'))
const UserRoutes = lazyload(() => import('./pages/user'))
const OrgRoutes = lazyload(() => import('./pages/org/org.routes'))
const ProjectRoutes = lazyload(() => import('./pages/project-v2'))
const PluginOauth2Callback = lazyload(() => import('./pages/org/plugins/PluginOauth2Callback'))

function PublicRoutesOnly({ children }: { children: React.ReactNode }) {
  const { userData } = useAuth()
  const redirectUrl = `${USER_ROUTE}/${USER_ORG_ROUTE}`

  return userData ? <Navigate to={redirectUrl} replace /> : children
}

function PrivateRoutesOnly({ children }: { children: React.ReactNode }) {
  const { userData } = useAuth()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const paramStr = searchParams.toString()
  const redirectUrl = paramStr ? `${LOGIN_ROUTE}/?${paramStr}` : LOGIN_ROUTE

  return userData ? children : <Navigate to={redirectUrl} replace state={{ from: location }} />
}

function FallbackRoute() {
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const paramStr = searchParams.toString()
  const to = paramStr ? `${USER_ROUTE}/${USER_ORG_ROUTE}?${paramStr}` : `${USER_ROUTE}/${USER_ORG_ROUTE}`

  return <Navigate to={to} replace state={{ from: location }} />
}

/**
 * Main Routes
 */
function MainRoutes() {
  const { orgs, isOrgsLoading } = useOrgsContext()

  return (
    <NotificationsProvider>
      <div id='dashboard' className='w-full h-full'>
        <ToolbarV2 />

        <div className='main block text-zinc-700 bg-[#f2f2f3] dark:bg-slate-800 dark:text-slate-300 text-m-m h-full'>
          {isOrgsLoading ? (
            <Loading />
          ) : orgs.length > 0 ? (
            <Suspense fallback={<Loading fullHeight />}>
              <Routes>
                <Route path={`${ORG_ROUTE}/${ORG_ID_PARAM}/*`} element={<OrgRoutes />} />
                <Route path={`${DUMMY_ROUTE}/*`} element={<SpreadSheetAuth />} />
                <Route path={`${USER_ROUTE}/*`} element={<UserRoutes />} />
                <Route path={`${PROJECT_ROUTE}/${PROJECT_ID_PARAM}/*`} element={<ProjectRoutes />} />
                <Route path='/plugins/oauth2/callback' element={<PluginOauth2Callback />} />
                <Route path='/*' element={<FallbackRoute />} />
              </Routes>
            </Suspense>
          ) : (
            <PageDashboardError />
          )}
        </div>

        <AppModals />
      </div>
    </NotificationsProvider>
  )
}

/**
 * App Routes
 */
export default function AppRoutes() {
  return (
    <Routes>
      <Route
        path={LOGIN_ROUTE}
        element={
          <PublicRoutesOnly>
            <LoginPage />
          </PublicRoutesOnly>
        }
      />
      <Route
        path='/*'
        element={
          <PrivateRoutesOnly>
            <MainRoutes />
          </PrivateRoutesOnly>
        }
      />
    </Routes>
  )
}
